import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource/bitter/cyrillic-700.css';
import '@fontsource-variable/caveat/wght.css';
import './styles/theme.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PlatformProvider, usePlatform } from './providers/PlatformProvider';
import { initSentry, setSentryPlatformTags, ErrorBoundary } from './observability/sentry';
import App from './App';

// TECH-1172: Initialize Sentry early (env-gated, no-op without VITE_SENTRY_DSN)
initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number } | undefined)?.status
          ?? Number(((error as { message?: string } | undefined)?.message?.match(/\d{3}/) ?? [])[0]);
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

/** Sets Sentry platform tag once PlatformAdapter is resolved (ADR-023). */
function SentryPlatformTags() {
  const adapter = usePlatform();
  useEffect(() => { setSentryPlatformTags(adapter); }, [adapter]);
  return null;
}

/**
 * Fallback UI for unrecoverable React errors (Sentry.ErrorBoundary).
 * Honest stub: no bot redirect (architectural-integrity.md section 7).
 */
function SentryFallback() {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: '#c33' }}>
      <h2>Что-то пошло не так</h2>
      <p>Ошибка отправлена разработчикам. Попробуйте перезагрузить приложение.</p>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary fallback={<SentryFallback />}>
      <PlatformProvider>
        <SentryPlatformTags />
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </PlatformProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
